import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog.jsx';
import { Button } from '../ui/button.jsx';
import { Input } from '../ui/input.jsx';
import { Label } from '../ui/label.jsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs.jsx';
import { Loader2, Lock, QrCode } from 'lucide-react';
import { useLanguage } from '../layout/LanguageSwitcher.jsx';
import { useDsBreakpoints } from '../ds/useDsBreakpoints.jsx';
import AuthApiCall from '../../../Apicall/AuthApiCall';
import { useUser } from '../../../context/UserContext';
import { postLoginRedirect } from '../../../utils/postLoginRedirect';
import CryptoJS from 'crypto-js';
import BankIdAuthModal from '../../../pages/Landing/BankIdAuthModal';
import { dsToast } from '../../utils/dsToast';
import { useBankIdReturn } from '../../../hooks/useBankIdReturn';
import { findPending, claimResume, claimReturnProblem, isIOSDevice } from '../../../utils/bankIdSession';
import { bankIdHints } from '../../../utils/bankIdHints';
const ENTRY_COPY = {
    login: { title: 'login.title', subtitle: 'login.subtitle' },
    start: { title: 'entry.start.title', subtitle: 'entry.start.subtitle' },
    buyer: { title: 'entry.buyer.title', subtitle: 'entry.start.subtitle' },
    seller: { title: 'entry.seller.title', subtitle: 'entry.start.subtitle' },
};
// bankIdScope: names the dialog that starts a BankID login, so after a reload / the BankID app's
// return only that dialog resumes it (several dialogs can be mounted on one page).
export function LoginDialog({ open, onOpenChange, intent = 'login', resumePending = false, afterLogin, bankIdScope = 'default' }) {
    const { t, languageCode } = useLanguage();
    const { isMobile } = useDsBreakpoints();
    const { refreshUserInfo } = useUser();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSignUp, setIsSignUp] = useState(false);
    // Sign-up offers two ways in: BankID (the account is made from the verified identity, nothing
    // to fill in) or e-mail (BankID is asked for later, before the first deal).
    const [method, setMethod] = useState('bankid');
    const [emailLoading, setEmailLoading] = useState(false);
    const [dialogView, setDialogView] = useState('main');
    const [bankIdTab, setBankIdTab] = useState(null);
    // True only when the user tapped a BankID button; a resume after returning from the
    // BankID app must not launch the app again.
    const [bankIdChosenByUser, setBankIdChosenByUser] = useState(false);
    // An order this browser already started, resumed after a reload or the BankID app's return.
    const [resumeOrderRef, setResumeOrderRef] = useState(null);
    const [error, setError] = useState('');
    // iPhone/iPad open the BankID app only from the user's own tap, and only with a token that
    // already exists. So while this dialog shows its BankID choices there, a same-device order
    // runs in the (hidden) BankID dialog and the "Open BankID" tap opens the app at once.
    const bankIdControl = useRef(null);
    const prestartBankId = open && dialogView === 'main' && bankIdTab === null && !resumeOrderRef && isIOSDevice();
    useBankIdReturn((returnResult) => {
        const pending = findPending({ purpose: 'auth', scope: bankIdScope });
        if (pending && claimResume(pending.orderRef)) {
            bankIdControl.current?.cancel();
            setBankIdChosenByUser(false);
            setResumeOrderRef(pending.orderRef);
            setBankIdTab(1);
            return;
        }
        // The return reached a browser that cannot finish it (another browser, expired, …).
        if (claimReturnProblem(returnResult)) {
            const hints = bankIdHints(languageCode);
            dsToast('error', { title: 'BankID', text: hints[returnResult.status] ?? hints.unknownOrder }, { autoClose: 12000 });
        }
    }, resumePending);
    useEffect(() => {
        if (!open) {
            setEmail('');
            setPassword('');
            setConfirmPassword('');
            setIsSignUp(false);
            setMethod('bankid');
            setError('');
            setDialogView('main');
        }
    }, [open]);
    const startBankId = (tab) => {
        if (prestartBankId) {
            // Same tap: open the app now (false if the order is not ready — the dialog then
            // shows its own button), or switch the running order to QR.
            if (tab === 1) bankIdControl.current?.launch();
            else bankIdControl.current?.selectTab(tab);
        }
        setResumeOrderRef(null);
        setBankIdChosenByUser(true);
        setBankIdTab(tab);
    };
    // isNewSignup: the account was just created — by the e-mail "create account" form, or by a
    // first BankID login (the server says so). A new BankID account goes to the same profile page
    // as an email sign-up, where only the e-mail is optional.
    const finishAuthentication = async (accessToken, { isNewSignup = false, viaBankId = false } = {}) => {
        localStorage.setItem('authToken', CryptoJS.AES.encrypt(accessToken, 'klargo-secret-key').toString());
        setBankIdTab(null);
        setResumeOrderRef(null);
        onOpenChange(false);
        await refreshUserInfo();
        if (afterLogin) await afterLogin({ isNewSignup });
        else if (isNewSignup && viaBankId) navigate('/CompleteProfile', { replace: true });
        else await postLoginRedirect(navigate);
    };
    const signUpToggle = (<div className="text-center text-[13px] -mt-1">
        <span className="text-muted-foreground">
          {isSignUp ? t('login.alreadyHaveAccount') : t('login.newToKlargo')}
        </span>
        <button type="button" className="font-medium text-foreground underline-offset-4 hover:underline focus-visible:underline" onClick={() => toggleAuthMode()}>
          {isSignUp ? t('login.logIn') : t('login.createAnAccount')}
        </button>
      </div>);
    const toggleAuthMode = () => {
        setIsSignUp(!isSignUp);
        setPassword('');
        setConfirmPassword('');
        setError('');
    };
    const handleEmailAuth = async (event) => {
        event.preventDefault();
        if (isSignUp && password !== confirmPassword) {
            setError(t('login.makePasswordsMatch'));
            return;
        }
        if (isSignUp && password.length < 6) {
            setError(t('login.passwordMinChars').replace('{count}', '6'));
            return;
        }
        setEmailLoading(true);
        setError('');
        try {
            if (isSignUp) {
                const payload = {
                    userName: email, emailAddress: email, personalContact: '', drivingLiscencePath: '', bankDetail: '',
                    password, profileImagePath: '', isSeller: false, isBuyer: false, bankIdSSN: '', surname: email,
                    name: email, country: 'SE', createExternalUserRegisterLogDto: {
                        identityToken: 'string', aud: 'string', authenticationInstant: 'string', authenticationMethod: 'string',
                        authenticationType: 'string', countary: 'string', exp: '1', familyName: 'string', given_Name: 'string',
                        givenName: 'string', iat: '1', identityScheme: 'string', ipAdress: 'string', iss: 'string', name: 'string',
                        nameIdentifier: 'string', nbf: '1', sessionIndex: 'string', ssn: 'string', sub: 'string', surName: 'string',
                        userEmail: email, userId: 0,
                    },
                };
                const registered = await AuthApiCall({ url: '/services/app/User/RegisterExternalUser', method: 'POST', data: payload });
                if (!registered.data?.success)
                    throw new Error(registered.data?.error?.message || t('login.signupFailed'));
            }
            const response = await AuthApiCall({ url: '/TokenAuth/Authenticate', method: 'POST', data: { userNameOrEmailAddress: email, password } });
            const token = response.data?.result?.accessToken;
            if (!token)
                throw new Error(response.data?.error?.message || t('login.loginFailed'));
            dsToast('success', {
                title: t(isSignUp ? 'login.accountCreated' : 'login.loginSuccessful'),
                text: t(isSignUp ? 'login.welcomeKlargo' : 'login.welcomeBack'),
            }, { position: 'bottom-right' });
            await finishAuthentication(token, { isNewSignup: isSignUp });
        }
        catch (failure) {
            setError(failure.response?.data?.error?.details || failure.response?.data?.error?.message || failure.message || t('login.tryAgain'));
        }
        finally {
            setEmailLoading(false);
        }
    };
    const renderBankIdContent = () => {
        if (dialogView === 'help') {
            return (<div className="space-y-4 py-4">
          <p className="text-sm font-medium text-foreground">{t('login.troubleLoggingIn')}</p>
          <div className="space-y-4 text-sm text-muted-foreground">
            <div>
              <p className="font-medium text-foreground mb-1">{t('login.bankidNotOpened')}</p>
              <p>{t('login.tryLaunchingManually')}</p>
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">{t('login.usingComputerOrTablet')}</p>
              <p>{t('login.scanQrSecure')}</p>
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">{t('login.stillHavingIssues')}</p>
              <button onClick={() => { onOpenChange(false); navigate("/hjalp"); }} className="text-foreground hover:underline font-medium">{t('login.contactSupport')}</button>
            </div>
          </div>
          <Button onClick={() => setDialogView('main')} variant="outline" className="w-full">
            {t('login.back')}
          </Button>
        </div>);
        }
        const primaryIsQr = !isMobile;
        const sameDeviceLabel = isMobile ? t('login.openBankid') : t('login.openBankidThisDevice');
        const qrAction = (variant) => variant === 'primary' ? (<Button key="qr" onClick={() => startBankId(0)} className="w-full font-semibold h-11 rounded-[9px] bg-ds-ink text-[15px] hover:bg-ds-ink-hover">
          <QrCode className="h-4 w-4 mr-2"/>
          {t('login.showQr')}
        </Button>) : (<Button key="qr" onClick={() => startBankId(0)} variant="outline" className="w-full h-11 font-semibold rounded-[9px] border-[color:var(--ds-rule-control)] text-ds-ink hover:bg-[rgba(20,36,46,.05)]">
          <QrCode className="h-4 w-4 mr-2"/>
          {t('login.showQr')}
        </Button>);
        const sameDeviceAction = (variant) => variant === 'primary' ? (<Button key="same-device" onClick={() => startBankId(1)} className="w-full font-semibold h-11 rounded-[9px] bg-ds-ink text-[15px] hover:bg-ds-ink-hover">
          {sameDeviceLabel}
        </Button>) : (<Button key="same-device" onClick={() => startBankId(1)} variant="outline" className="w-full h-11 font-semibold rounded-[9px] border-[color:var(--ds-rule-control)] text-ds-ink hover:bg-[rgba(20,36,46,.05)]">
          {sameDeviceLabel}
        </Button>);
        return (<>
        
        <div className="flex items-center gap-2 text-[13px] text-muted-foreground mb-4">
          <Lock className="h-4 w-4 shrink-0 text-muted-foreground"/>
          
          <p>{t(isSignUp ? 'login.bankidSignupHint' : intent === 'login' ? 'login.secureBankid' : 'entry.start.trust')}</p>
        </div>

        <div className="space-y-3">
          {primaryIsQr ? qrAction('primary') : sameDeviceAction('primary')}

          
          <div className="relative py-1" aria-hidden="true">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t"/>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-ds-sheet px-2 text-[12px] font-normal normal-case tracking-normal text-muted-foreground">
                {t('or').toLocaleLowerCase('sv-SE')}
              </span>
            </div>
          </div>

          {primaryIsQr ? sameDeviceAction('secondary') : qrAction('secondary')}

          <div className="-mt-1 text-center">
            <button onClick={() => setDialogView('help')} className="inline-flex min-h-10 items-center justify-center px-3 text-[12px] font-normal text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:underline">
              {t('login.needHelp')}
            </button>
          </div>

          {signUpToggle}

        </div>
      </>);
    };
    const renderEmailContent = () => (<form onSubmit={handleEmailAuth} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="email" className="text-sm font-medium text-foreground">
          {t('login.email')}
        </Label>
        <Input id="email" type="email" autoComplete="email" placeholder="your.email@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11 bg-white"/>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="password" className="text-sm font-medium text-foreground">
          {t('login.password')}
        </Label>
        <Input id="password" type="password" autoComplete={isSignUp ? 'new-password' : 'current-password'} placeholder={isSignUp ? t('login.atLeast6Chars') : t('login.enterPassword')} value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11 bg-white"/>
      </div>

      {isSignUp && (<div className="space-y-1.5">
          <Label htmlFor="confirmPassword" className="text-sm font-medium text-foreground">
            {t('login.confirmPassword')}
          </Label>
          <Input id="confirmPassword" type="password" autoComplete="new-password" placeholder={t('login.reenterPassword')} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="h-11 bg-white"/>
        </div>)}

      
      <div className="pt-1">
        <Button type="submit" className="w-full font-semibold h-11 rounded-[9px] bg-ds-ink text-[15px] hover:bg-ds-ink-hover" disabled={emailLoading}>
          {emailLoading ? (<>
              <Loader2 className="mr-2 h-4 w-4 animate-spin"/>
              {isSignUp ? t('login.creatingAccount') : t('login.loggingIn')}
            </>) : (isSignUp ? t('login.createAccount') : t('login.logIn'))}
        </Button>
      </div>

      
      {!isSignUp && (<div className="-mt-1 text-center">
          <button type="button" className="inline-flex min-h-10 items-center justify-center px-3 text-[12px] font-normal text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:underline" onClick={() => { onOpenChange(false); navigate("/hjalp"); }}>
            {t('login.forgotPassword')}
          </button>
        </div>)}

      {isSignUp && <p className="text-[12px] text-center text-muted-foreground">{t('login.emailSignupHint')}</p>}

      <div className={isSignUp ? 'pt-1' : ''}>{signUpToggle}</div>
    </form>);
    return (<><Dialog open={open && bankIdTab === null} onOpenChange={onOpenChange}>
      <DialogContent overlayClassName="bg-black/50" closeLabel={t('common.close')} className="ds-scope font-ds-sans sm:max-w-[480px] p-0 bg-ds-sheet border border-ds-rule-edge shadow-[0_8px_32px_rgba(16,30,40,0.16)] rounded-[13px]">
        
        <div className="p-6">
          <DialogHeader className="space-y-2 mb-6">
            <DialogTitle className="font-ds-serif text-[26px] font-bold tracking-[-0.01em] text-ds-ink text-center">
              {t(isSignUp ? 'login.signupTitle' : ENTRY_COPY[intent].title)}
            </DialogTitle>
            <DialogDescription className="text-[15px] text-ds-text text-center">
              {t(isSignUp ? 'login.signupSubtitle' : ENTRY_COPY[intent].subtitle)}
            </DialogDescription>
          </DialogHeader>

          {error && <p role="alert" className="mb-4 text-sm text-ds-red-fg">{error}</p>}<Tabs value={method} onValueChange={setMethod} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-muted h-11 p-1 rounded-lg mb-6">
              <TabsTrigger value="bankid" className="text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-foreground data-[state=active]:shadow-sm">
                {t(isSignUp ? 'login.continueWithBankid' : 'login.bankid')}
              </TabsTrigger>
              <TabsTrigger value="email" className="text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-foreground data-[state=active]:shadow-sm">
                {t(isSignUp ? 'login.continueWithEmail' : 'login.emailPassword')}
              </TabsTrigger>
            </TabsList>

            
            <TabsContent value="bankid" className="space-y-4 mt-0 focus-visible:ring-0">
              {renderBankIdContent()}
            </TabsContent>

            <TabsContent value="email" className="space-y-4 mt-0 focus-visible:ring-0">
              {renderEmailContent()}
            </TabsContent>
          </Tabs>

          
          <div className="mt-3 pt-3 border-t">
            <p className="text-[11px] text-center text-muted-foreground leading-relaxed">
              {t('login.termsText')}{' '}
              <a href="/villkor" className="text-foreground underline underline-offset-2">{t('login.termsOfService')}</a>
              {' '}{t('and')}{' '}
              <a href="/integritet" className="text-foreground underline underline-offset-2">{t('login.privacyPolicy')}</a>.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog><BankIdAuthModal key={resumeOrderRef ?? 'new'} open={bankIdTab !== null || prestartBankId} hidden={bankIdTab === null} controlRef={bankIdControl} initialTab={bankIdTab} autoLaunch={bankIdChosenByUser && bankIdTab === 1} resumeOrderRef={resumeOrderRef} scope={bankIdScope} onClose={() => { setBankIdTab(null); setResumeOrderRef(null); }} onSuccess={(token, info) => finishAuthentication(token, { isNewSignup: info?.isNewUser === true, viaBankId: true })}/></>);
}
;
